import { yupResolver } from "@hookform/resolvers/yup"
import { Typography } from "@jewlr/storybook/core"
import PropTypes from "prop-types"
import React from "react"
import { useForm } from "react-hook-form"
import { useSearchParams } from "react-router-dom"

import {
  ButtonLink,
  CancelButton,
  FormContainer,
  InputWrapper,
  SubmitButton,
} from "areas/order/components"
import ContactInformation from "areas/order/contact-information"
import processOrder from "areas/order/helper/process-order"
import { joinNames, splitName } from "helpers/name"

import Heading from "../heading"

import ChooseSize from "./choose-size"
import ExchangeFees from "./exchange-fees"
import ResizeFees from "./resize-fees"
import ShipAddress from "./ship-address"
import validationSchema from "./validate"

const OrderForm = ({ orderData }) => {
  const [searchParams] = useSearchParams()
  const email = searchParams.get("email")
  const name = searchParams.get("name")
  const phone = searchParams.get("phone")

  const {
    control,
    formState: { errors, isDirty, isSubmitting, isValid },
    handleSubmit,
    setError,
    setValue,
    watch,
  } = useForm({
    defaultValues: {
      address1: orderData.shipping_info.address1 || "",
      address2: orderData.shipping_info.address2 || "",
      city: orderData.shipping_info.city || "",
      contact_email: email || orderData.order.email || "",
      contact_name: orderData.order.ship_address.first_name
        ? joinNames(
            orderData.order.ship_address.first_name,
            orderData.order.ship_address.last_name
          )
        : name || "",
      contact_phone_num: phone || orderData.order.ship_address.phone || "",
      new_ring_size: "",
      postal_code: orderData.shipping_info.postal_code || "",
      rma_id: "",
      state: orderData.shipping_info.state || "",
    },
    mode: "onBlur",
    resolver: yupResolver(validationSchema(orderData)),
  })

  const onSubmit = async (values) => {
    const formValues = {
      ...values,
      address: {
        address1: values.address1,
        address2: values.address2,
        city: values.city,
        country: orderData.shipping_info.country,
        postal_code: values.postal_code,
        state: values.state,
      },
      contact_first_name: splitName(values.contact_name).first,
      contact_last_name: splitName(values.contact_name).last,
      jewlr4: true,
      lid: values.rma_id,
      new_item_size: {
        [values.rma_id]: values.new_ring_size,
      },
      new_item_size_tungsten: {
        [values.rma_id]: values.new_ring_size,
      },
      order_number: orderData.order.number,
    }
    try {
      const response = await processOrder(formValues, "process_resizing")
      if (response.data?.redirect_url) {
        window.location.href = response.data.redirect_url
      }
    } catch (err) {
      console.log(err)
      setError("root.error", {
        message: err?.errors?.[0] || err?.message || err,
      })
    }
  }

  const item = orderData.order.line_items.find((li) => li.id == watch("rma_id"))

  const selectedSize =
    item &&
    item.resize_info.size_options &&
    item.resize_info.size_options.find(
      (sz) => sz.value === watch("new_ring_size")
    )
  console.log("debug selectedSize", selectedSize)

  return (
    <FormContainer>
      <Heading rmaType="resize" />
      <form onSubmit={handleSubmit(onSubmit)}>
        <ChooseSize
          control={control}
          item={item}
          orderData={orderData}
          selectedSizeValue={watch("new_ring_size")}
          setValue={setValue}
        />

        {selectedSize ? (
          <>
            <Typography
              fontSize="18px"
              fontWeight="500"
              lineHeight="20px"
              py={2}
            >
              Fees
            </Typography>
            {item.is_exchangable ? (
              <ExchangeFees item={item} selectedSize={selectedSize} />
            ) : (
              <ResizeFees item={item} selectedSize={selectedSize} />
            )}
            <ContactInformation control={control} />
            <ShipAddress control={control} shipData={orderData.shipping_info} />

            <InputWrapper>
              <SubmitButton
                disabled={!(isValid && isDirty)}
                loading={isSubmitting}
              >
                Process Request
              </SubmitButton>
            </InputWrapper>
            <InputWrapper>
              <CancelButton data-lc="order-form" to="/">
                Cancel and Continue Shopping
              </CancelButton>
            </InputWrapper>
          </>
        ) : (
          <InputWrapper mt={8}>
            <ButtonLink data-lc="order-form" outline to="/">
              Cancel and Continue Shopping
            </ButtonLink>
          </InputWrapper>
        )}
        {errors?.root?.error && (
          <Typography color="error" my={2}>
            {errors.root.error.message}
          </Typography>
        )}
      </form>
    </FormContainer>
  )
}

OrderForm.propTypes = {
  orderData: PropTypes.object,
}

export default OrderForm
